import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../api/types";

/**
 * Minimal async-resource hook — the seam where `@norkron/web-query` (TanStack +
 * zod) plugs in later. Returns { data, error, loading, reload } and cancels
 * stale results on dependency change. Keeps loading/empty/error rendering
 * consistent across every screen.
 */

export interface AsyncResult<T> {
  data: T | null;
  error: ApiError | Error | null;
  loading: boolean;
  reload: () => void;
}

export function useAsync<T>(
  fn: () => Promise<T>,
  deps: unknown[],
): AsyncResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const latest = useRef(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const ticket = ++latest.current;
    // Reset to a clean loading state whenever the key (deps) changes. Without
    // clearing `data`, a viewer whose route param changed (e.g. /doc/:id → a
    // different :id) would briefly paint the PREVIOUS document under the new
    // URL, and a fetch that never resolved would leave stale/blank content on
    // screen. Clearing here guarantees the loading gate (Spinner/skeleton)
    // shows until the fetch for THIS key settles.
    setData(null);
    setLoading(true);
    setError(null);
    fn()
      .then((result) => {
        if (ticket === latest.current) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (ticket === latest.current) {
          setError(e as Error);
          setLoading(false);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload };
}
